import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';

const API_BASE = process.env.REACT_APP_API_URL;

const TYPE_META = {
  document:  { label: 'Document',  color: '#46BB89', icon: '📄' },
  process:   { label: 'Process Flow', color: '#60a5fa', icon: '🏭' },
  jig:       { label: 'Jig',       color: '#a78bfa', icon: '🔩' },
  equipment: { label: 'Equipment', color: '#f59e0b', icon: '⚙️' },
  test:      { label: 'Test',      color: '#34d399', icon: '🧪' },
};

export default function QualitySearchModal({ onClose }) {
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const search = useCallback(async (query) => {
    if (query.length < 2) { setResults([]); return; }
    setLoading(true);
    try {
      const token = await getToken();
      const res = await window.fetch(`${API_BASE}/quality/search?q=${encodeURIComponent(query)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.ok) { setResults(data.results || []); setActive(0); }
    } catch {} finally { setLoading(false); }
  }, [getToken]);

  useEffect(() => {
    const t = setTimeout(() => search(q), 220);
    return () => clearTimeout(t);
  }, [q, search]);

  const go = (result) => {
    navigate(result.route);
    onClose();
  };

  const handleKey = (e) => {
    if (e.key === 'Escape') { onClose(); return; }
    if (e.key === 'ArrowDown') { setActive(a => Math.min(a + 1, results.length - 1)); e.preventDefault(); return; }
    if (e.key === 'ArrowUp')   { setActive(a => Math.max(a - 1, 0)); e.preventDefault(); return; }
    if (e.key === 'Enter' && results[active]) { go(results[active]); }
  };

  const statusColor = (s) => {
    if (s === 'Released') return '#46BB89';
    if (s === 'In Review') return '#60a5fa';
    if (s === 'Obsolete') return '#EC2124';
    return '#94a3b8';
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', zIndex:2000,
      display:'flex', alignItems:'flex-start', justifyContent:'center', paddingTop:80 }}
      onClick={onClose}>
      <div style={{ background:'var(--ni-char)', border:'1px solid var(--ni-border-soft)', borderRadius:12,
        width:600, maxWidth:'92vw', boxShadow:'0 24px 48px rgba(0,0,0,0.5)' }}
        onClick={e => e.stopPropagation()}>

        {/* Search input */}
        <div style={{ display:'flex', alignItems:'center', padding:'14px 16px',
          borderBottom: results.length > 0 || loading ? '1px solid var(--ni-border-soft)' : 'none' }}>
          <span style={{ fontSize:18, marginRight:10, color:'var(--ni-text-muted)' }}>🔍</span>
          <input
            ref={inputRef}
            value={q}
            onChange={e => setQ(e.target.value)}
            onKeyDown={handleKey}
            placeholder="Search quality documents, flows, jigs, equipment, tests…"
            style={{ flex:1, background:'transparent', border:'none', outline:'none',
              color:'var(--ni-text)', fontSize:15, fontFamily:'inherit' }}
          />
          {loading && <span style={{ fontSize:12, color:'var(--ni-text-muted)', marginLeft:8 }}>…</span>}
          <button onClick={onClose} style={{ background:'none', border:'none', color:'var(--ni-text-muted)',
            cursor:'pointer', fontSize:18, marginLeft:8 }}>×</button>
        </div>

        {/* Results */}
        {results.length > 0 && (
          <div style={{ maxHeight:400, overflowY:'auto' }}>
            {results.map((r, i) => {
              const meta = TYPE_META[r.type] || { label: r.type, color: '#666', icon: '📋' };
              return (
                <div key={`${r.type}-${r.id}`}
                  onClick={() => go(r)}
                  onMouseEnter={() => setActive(i)}
                  style={{ display:'flex', alignItems:'center', gap:12, padding:'11px 16px',
                    cursor:'pointer', background: i === active ? 'rgba(255,255,255,0.06)' : 'transparent',
                    borderBottom:'1px solid rgba(255,255,255,0.04)' }}>
                  <span style={{ fontSize:16, flexShrink:0 }}>{meta.icon}</span>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontWeight:600, fontSize:13, whiteSpace:'nowrap',
                      overflow:'hidden', textOverflow:'ellipsis' }}>{r.name}</div>
                    <div style={{ fontSize:11, color:'var(--ni-text-muted)', fontFamily:'monospace' }}>{r.docNumber}</div>
                  </div>
                  <span style={{ fontSize:11, fontWeight:700, color: meta.color, background: meta.color+'18',
                    borderRadius:4, padding:'2px 7px', flexShrink:0 }}>{meta.label}</span>
                  <span style={{ fontSize:11, fontWeight:600, color: statusColor(r.status),
                    flexShrink:0, minWidth:60, textAlign:'right' }}>{r.status}</span>
                </div>
              );
            })}
          </div>
        )}

        {/* Empty state */}
        {!loading && q.length >= 2 && results.length === 0 && (
          <div style={{ padding:'24px 16px', textAlign:'center', color:'var(--ni-text-muted)', fontSize:13 }}>
            No results for "{q}"
          </div>
        )}

        {/* Hint */}
        {q.length === 0 && (
          <div style={{ padding:'16px', color:'var(--ni-text-muted)', fontSize:12 }}>
            Search across quality documents, process flows, jigs, equipment, and test procedures.
            Press <kbd style={{ background:'rgba(255,255,255,0.1)', borderRadius:3,
              padding:'1px 5px', fontFamily:'monospace', fontSize:11 }}>↑↓</kbd> to navigate,{' '}
            <kbd style={{ background:'rgba(255,255,255,0.1)', borderRadius:3,
              padding:'1px 5px', fontFamily:'monospace', fontSize:11 }}>Enter</kbd> to open,{' '}
            <kbd style={{ background:'rgba(255,255,255,0.1)', borderRadius:3,
              padding:'1px 5px', fontFamily:'monospace', fontSize:11 }}>Esc</kbd> to close.
          </div>
        )}
      </div>
    </div>
  );
}
